import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import RemindersPanel from '../components/RemindersPanel'
import { toDateStr } from '../lib/dates'

const STATUS_COLOR = { Open: '#d97706', Converted: '#16a34a', Archived: '#6b7280' }
const STATUS_OPTIONS = ['Open', 'Converted', 'Archived']
const SERVICE_TYPES = ['Landscaping', 'Cleanup', 'Irrigation', 'Tree Work', 'Hardscape', 'Maintenance', 'Other']
const URGENCY_OPTIONS = ['ASAP', 'This Week', 'This Month', 'Flexible']
const SOURCE_OPTIONS = ['Referral', 'Repeat Client', 'Google', 'Facebook', 'Yard Sign', 'Other']
const URGENCY_COLOR = { ASAP: '#dc2626', 'This Week': '#d97706' }

// ─── SiteVisitsList ─────────────────────────────────────────────────────────────
export function SiteVisitsList() {
  const [searchParams] = useSearchParams()
  const { profile, isAdmin } = useAuth()
  const [visits, setVisits] = useState([])
  const [clients, setClients] = useState([])
  const [clientFilter, setClientFilter] = useState(searchParams.get('client') || '')
  const [statusFilter, setStatusFilter] = useState('Open')
  const [todayOnly, setTodayOnly] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchAll = useCallback(async () => {
    const visitsQuery = isAdmin
      ? supabase.from('site_visits').select('*, client:clients(id, name)').order('created_at', { ascending: false })
      : supabase.from('site_visits').select('*, client:clients(id, name)').eq('created_by', profile?.id).order('created_at', { ascending: false })
    const [visitsRes, clientsRes] = await Promise.all([
      visitsQuery,
      supabase.from('clients').select('id, name').order('name'),
    ])
    if (visitsRes.error) setError(visitsRes.error.message)
    setVisits(visitsRes.data || [])
    setClients(clientsRes.data || [])
    setLoading(false)
  }, [isAdmin, profile])

  useEffect(() => { fetchAll() }, [fetchAll])

  const todayStr = toDateStr(new Date())
  const filtered = visits
    .filter(v => {
      const matchClient = !clientFilter || v.client_id === clientFilter
      const matchStatus = statusFilter === 'all' || v.status === statusFilter
      const matchToday = !todayOnly || v.scheduled_date === todayStr
      return matchClient && matchStatus && matchToday
    })
    .sort((a, b) => {
      if (todayOnly) return (a.start_time || '').localeCompare(b.start_time || '')
      return new Date(b.created_at) - new Date(a.created_at)
    })

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Site Visits</h1>
          <p style={s.sub}>{visits.filter(v => v.status === 'Open').length} open</p>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}

      <div style={s.filters}>
        <select style={s.filterSelect} value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {[...STATUS_OPTIONS, 'all'].map(f => (
          <button key={f} style={{ ...s.filterBtn, ...(statusFilter === f ? s.filterActive : {}) }} onClick={() => setStatusFilter(f)}>
            {f === 'all' ? 'All' : f}
          </button>
        ))}
        <button style={{ ...s.filterBtn, ...(todayOnly ? s.filterActive : {}) }} onClick={() => setTodayOnly(t => !t)}>
          📅 Scheduled Today
        </button>
      </div>

      {loading ? <div style={s.loading}>Loading...</div> : (
        <div style={s.list}>
          {filtered.map(v => (
            <Link to={`/site-visits/${v.id}`} key={v.id} style={s.card}>
              <div style={s.info}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={s.name}>{v.title || `Site Visit — ${v.client?.name || 'Unknown Client'}`}</span>
                  {v.urgency && URGENCY_COLOR[v.urgency] && (
                    <span style={{ ...s.urgencyBadge, color: URGENCY_COLOR[v.urgency], background: URGENCY_COLOR[v.urgency] + '1a' }}>{v.urgency}</span>
                  )}
                </div>
                <span style={s.detail}>
                  {v.client?.name || 'Unknown Client'}{v.service_type ? ` · ${v.service_type}` : ''}
                  {v.scheduled_date ? ` · 📅 ${new Date(v.scheduled_date + 'T12:00:00').toLocaleDateString()}${v.start_time ? ` ${v.start_time}` : ''}` : ` · ${new Date(v.created_at).toLocaleDateString()}`}
                </span>
              </div>
              <span style={{ ...s.statusBadge, background: STATUS_COLOR[v.status] || '#6b7280' }}>{v.status}</span>
            </Link>
          ))}
          {filtered.length === 0 && <div style={s.empty}>No site visits found.</div>}
        </div>
      )}
    </div>
  )
}

// ─── SiteVisitDetail ────────────────────────────────────────────────────────────
export function SiteVisitDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile, isAdmin } = useAuth()

  const [visit, setVisit] = useState(null)
  const [items, setItems] = useState([])
  const [photos, setPhotos] = useState([])
  const [employees, setEmployees] = useState([])
  const [resolvedJob, setResolvedJob] = useState(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(null) // itemId being uploaded to, or 'general'
  const [saving, setSaving] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    title: '', notes: '', service_type: '', urgency: '', source: '', site_address: '',
    scheduled_date: '', start_time: '', assigned_to: '',
  })
  const generalFileRef = useRef()
  const itemFileRefs = useRef({})

  const fetchAll = useCallback(async () => {
    const [visitRes, itemsRes, photosRes, employeesRes] = await Promise.all([
      supabase.from('site_visits').select('*, client:clients(id, name, phone, email, address)').eq('id', id).maybeSingle(),
      supabase.from('site_visit_items').select('*').eq('site_visit_id', id).order('sort_order').order('created_at'),
      supabase.from('site_visit_photos').select('*').eq('site_visit_id', id).order('created_at'),
      supabase.from('profiles').select('id, full_name, active').order('full_name'),
    ])
    if (visitRes.error) { setError(visitRes.error.message); setLoading(false); return }
    if (!visitRes.data) { setLoading(false); return }
    setVisit(visitRes.data)
    setForm({
      title: visitRes.data.title || '', notes: visitRes.data.notes || '',
      service_type: visitRes.data.service_type || '', urgency: visitRes.data.urgency || '',
      source: visitRes.data.source || '', site_address: visitRes.data.site_address || '',
      scheduled_date: visitRes.data.scheduled_date || '', start_time: visitRes.data.start_time || '',
      assigned_to: visitRes.data.assigned_to || '',
    })
    setItems(itemsRes.data || [])
    setPhotos(photosRes.data || [])
    setEmployees(employeesRes.data || [])

    // Resolve the resulting job -- either the direct convert-to-job path, or
    // (if none) the estimate this visit became, and that estimate's resulting job.
    if (visitRes.data.converted_to_job_id) {
      const { data: jobData } = await supabase.from('jobs').select('id, title').eq('id', visitRes.data.converted_to_job_id).maybeSingle()
      setResolvedJob(jobData || null)
    } else if (visitRes.data.converted_to_estimate_id) {
      const { data: jobData } = await supabase.from('jobs').select('id, title').eq('estimate_id', visitRes.data.converted_to_estimate_id).maybeSingle()
      setResolvedJob(jobData || null)
    } else {
      setResolvedJob(null)
    }
    setLoading(false)
  }, [id])

  useEffect(() => { fetchAll() }, [fetchAll])

  const dirty = visit && (
    form.title !== (visit.title || '') || form.notes !== (visit.notes || '') ||
    form.service_type !== (visit.service_type || '') || form.urgency !== (visit.urgency || '') ||
    form.source !== (visit.source || '') || form.site_address !== (visit.site_address || '') ||
    form.scheduled_date !== (visit.scheduled_date || '') || form.start_time !== (visit.start_time || '') ||
    form.assigned_to !== (visit.assigned_to || '')
  )

  async function saveDetails() {
    setSaving(true); setError('')
    const { error: err } = await supabase.from('site_visits').update({
      title: form.title.trim() || null,
      notes: form.notes.trim() || null,
      service_type: form.service_type || null,
      urgency: form.urgency || null,
      source: form.source || null,
      site_address: form.site_address.trim() || null,
      scheduled_date: form.scheduled_date || null,
      start_time: form.start_time.trim() || null,
      assigned_to: form.assigned_to || null,
    }).eq('id', id)
    if (err) { setError('Save failed: ' + err.message); setSaving(false); return }
    setSaving(false)
    fetchAll()
  }

  async function uploadPhoto(e, itemId = null) {
    const files = Array.from(e.target.files)
    if (!files.length) return
    setUploading(itemId || 'general'); setError('')
    for (const file of files) {
      const ext = file.name.split('.').pop()
      const path = `site-visits/${id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`
      const { error: upErr } = await supabase.storage.from('site-visit-photos').upload(path, file)
      if (upErr) { setError(`Photo upload failed: ${upErr.message}`); continue }
      const { data: { publicUrl } } = supabase.storage.from('site-visit-photos').getPublicUrl(path)
      const { error: insErr } = await supabase.from('site_visit_photos').insert([{
        site_visit_id: id, site_visit_item_id: itemId, url: publicUrl, path, uploaded_by: profile?.id,
      }])
      if (insErr) setError(`Photo save failed: ${insErr.message}`)
    }
    setUploading(null)
    fetchAll()
    const ref = itemId ? itemFileRefs.current[itemId] : generalFileRef.current
    if (ref) ref.value = ''
  }

  // ── Task items ──────────────────────────────────────────────────────────────
  async function addTask() {
    const { error: err } = await supabase.from('site_visit_items').insert([{
      site_visit_id: id, title: '', description: '', sort_order: items.length,
    }])
    if (err) { setError('Failed to add task: ' + err.message); return }
    fetchAll()
  }

  function updateTaskLocal(itemId, field, value) {
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, [field]: value } : it))
  }

  async function saveTaskField(itemId, field, value) {
    const { error: err } = await supabase.from('site_visit_items').update({ [field]: value }).eq('id', itemId)
    if (err) setError('Failed to save task: ' + err.message)
  }

  async function removeTask(itemId) {
    if (!window.confirm('Delete this task and its photos?')) return
    const { error: err } = await supabase.from('site_visit_items').delete().eq('id', itemId)
    if (err) { setError('Failed to delete task: ' + err.message); return }
    fetchAll()
  }

  // ── Lifecycle: archive / restore / delete ───────────────────────────────────
  async function toggleArchive() {
    setArchiving(true)
    const nextStatus = visit.status === 'Archived' ? 'Open' : 'Archived'
    const { error: err } = await supabase.from('site_visits').update({ status: nextStatus }).eq('id', id)
    if (err) { setError('Failed to update status: ' + err.message); setArchiving(false); return }
    setArchiving(false)
    fetchAll()
  }

  async function deleteVisit() {
    if (!window.confirm('Permanently delete this site visit, its tasks, and all photos? This cannot be undone.')) return
    setDeleting(true)
    const { error: err } = await supabase.from('site_visits').delete().eq('id', id)
    if (err) { setError('Failed to delete: ' + err.message); setDeleting(false); return }
    navigate('/site-visits')
  }

  function convertToEstimate() {
    if (!visit.client_id) { setError('This site visit has no client — cannot convert to an estimate.'); return }
    const params = new URLSearchParams()
    params.set('client', visit.client_id)
    params.set('site_visit', visit.id)
    if (visit.notes) params.set('notes', encodeURIComponent(visit.notes))
    navigate(`/estimates/new?${params.toString()}`)
  }

  function convertToJob() {
    if (!visit.client_id) { setError('This site visit has no client — cannot convert to a job.'); return }
    const params = new URLSearchParams()
    params.set('client', visit.client_id)
    params.set('site_visit', visit.id)
    const taskSummary = items.filter(t => t.title).map(t => `- ${t.title}${t.description ? `: ${t.description}` : ''}`).join('\n')
    const combinedNotes = [visit.notes, taskSummary].filter(Boolean).join('\n\n')
    if (combinedNotes) params.set('notes', encodeURIComponent(combinedNotes))
    navigate(`/jobs/new?${params.toString()}`)
  }

  if (loading) return <div style={s.loading}>Loading...</div>
  if (!visit) return <div style={s.empty}>{error || 'Site visit not found.'}</div>

  const generalPhotos = photos.filter(p => !p.site_visit_item_id)

  return (
    <div style={s.page}>
      <button style={s.back} onClick={() => navigate('/site-visits')}>← Back to Site Visits</button>

      {error && <div style={s.error}>{error}</div>}

      <div style={s.profileHeader}>
        <div style={s.profileInfo}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <h1 style={s.profileName}>{visit.title || 'Untitled Site Visit'}</h1>
            <span style={{ ...s.statusBadge, background: STATUS_COLOR[visit.status] || '#6b7280' }}>{visit.status}</span>
          </div>
          {visit.client && <Link to={`/clients/${visit.client_id}`} style={s.clientLink}>{visit.client.name}</Link>}
          <div style={s.contactRow}>
            {visit.client?.phone && <span>📞 {visit.client.phone}</span>}
            {(visit.site_address || visit.client?.address) && <span>📍 {visit.site_address || visit.client.address}</span>}
            {visit.scheduled_date && (
              <span>📅 {new Date(visit.scheduled_date + 'T12:00:00').toLocaleDateString()}{visit.start_time ? ` at ${visit.start_time}` : ''}</span>
            )}
            {visit.assigned_to && (
              <span>👤 {employees.find(e => e.id === visit.assigned_to)?.full_name || 'Assigned'}</span>
            )}
          </div>
        </div>
        <div style={s.headerActions}>
          {visit.status === 'Open' && isAdmin && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={s.addJobBtn} onClick={convertToEstimate}>→ Convert to Estimate</button>
              <button style={{ ...s.addJobBtn, background: '#0369a1' }} onClick={convertToJob}>→ Convert to Job</button>
            </div>
          )}
          {visit.status === 'Open' && !isAdmin && (
            <span style={s.internalNote}>An admin can convert this to an estimate or job.</span>
          )}
          {visit.status === 'Converted' && visit.converted_to_estimate_id && isAdmin && (
            <Link to={`/estimates/${visit.converted_to_estimate_id}/edit`} style={s.addJobBtn}>View Estimate</Link>
          )}
          {resolvedJob && (
            <Link to={`/jobs/${resolvedJob.id}`} style={{ ...s.addJobBtn, background: '#2563eb' }}>View Job</Link>
          )}
          {isAdmin && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={s.archiveBtn} onClick={toggleArchive} disabled={archiving}>
                {archiving ? '...' : visit.status === 'Archived' ? '↩ Restore' : '🗄 Archive'}
              </button>
              <button style={s.deleteBtn} onClick={deleteVisit} disabled={deleting}>
                {deleting ? '...' : '🗑️ Delete'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ ...s.section, marginBottom: '16px' }}>
        <div style={s.sectionHeader}>
          <h2 style={s.sectionTitle}>Request Details</h2>
          {dirty && <button style={s.saveBtn} onClick={saveDetails} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>}
        </div>
        <div style={s.field}>
          <label style={s.label}>Title</label>
          <input style={s.input} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Front & back yard walk" />
        </div>
        <div style={s.introGrid}>
          <div style={s.field}>
            <label style={s.label}>Service Type</label>
            <select style={s.input} value={form.service_type} onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))}>
              <option value="">Select...</option>
              {SERVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={s.field}>
            <label style={s.label}>Urgency</label>
            <select style={s.input} value={form.urgency} onChange={e => setForm(f => ({ ...f, urgency: e.target.value }))}>
              <option value="">Select...</option>
              {URGENCY_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={s.field}>
            <label style={s.label}>Source</label>
            <select style={s.input} value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}>
              <option value="">Select...</option>
              {SOURCE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div style={{ ...s.field, marginTop: '16px' }}>
          <label style={s.label}>Site Address (if different from client address)</label>
          <input style={s.input} value={form.site_address} onChange={e => setForm(f => ({ ...f, site_address: e.target.value }))} placeholder={visit.client?.address || 'Property address...'} />
        </div>
        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
          <label style={{ ...s.label, marginBottom: '10px' }}>Scheduling</label>
          <div style={s.introGrid}>
            <div style={s.field}>
              <label style={s.label}>Scheduled Date</label>
              <input style={s.input} type="date" value={form.scheduled_date} onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Start Time</label>
              <input style={s.input} type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Assigned To</label>
              <select style={s.input} value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}>
                <option value="">Unassigned</option>
                {employees.filter(e => e.active !== false || e.id === form.assigned_to).map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div style={{ ...s.field, marginTop: '16px' }}>
          <label style={s.label}>General Notes</label>
          <textarea
            style={s.textarea}
            rows={3}
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Anything that doesn't belong to a specific task below..."
          />
        </div>
      </div>

      <div style={{ ...s.section, marginBottom: '16px' }}>
        <div style={s.sectionHeader}>
          <h2 style={s.sectionTitle}>Tasks ({items.length})</h2>
          <button style={s.uploadBtn} onClick={addTask}>+ Add Task</button>
        </div>
        <p style={s.internalNote}>🔒 Internal only — never shown to clients. Each task keeps its own note and photos, and becomes its own section when converted to an estimate.</p>
        {items.length === 0 ? (
          <p style={s.empty2}>No tasks yet. Break the visit into pieces — "Trim hedges," "Fix irrigation zone 3" — so pricing and photos stay organized per task.</p>
        ) : (
          <div style={s.taskList}>
            {items.map(item => {
              const taskPhotos = photos.filter(p => p.site_visit_item_id === item.id)
              return (
                <div key={item.id} style={s.taskCard}>
                  <div style={s.taskCardHeader}>
                    <input
                      style={s.taskTitleInput}
                      value={item.title || ''}
                      placeholder="Task title..."
                      onChange={e => updateTaskLocal(item.id, 'title', e.target.value)}
                      onBlur={e => saveTaskField(item.id, 'title', e.target.value.trim() || null)}
                    />
                    <button style={s.taskRemoveBtn} onClick={() => removeTask(item.id)} title="Delete task">✕</button>
                  </div>
                  <textarea
                    style={s.taskTextarea}
                    rows={2}
                    placeholder="What needs doing here..."
                    value={item.description || ''}
                    onChange={e => updateTaskLocal(item.id, 'description', e.target.value)}
                    onBlur={e => saveTaskField(item.id, 'description', e.target.value.trim() || null)}
                  />
                  <div style={s.taskPhotoRow}>
                    {taskPhotos.map(p => (
                      <a key={p.id} href={p.url} target="_blank" rel="noreferrer">
                        <img src={p.url} alt="Task" style={s.taskPhoto} />
                      </a>
                    ))}
                    <button style={s.taskAddPhotoBtn} onClick={() => itemFileRefs.current[item.id]?.click()} disabled={uploading === item.id}>
                      {uploading === item.id ? '...' : '📷 +'}
                    </button>
                    <input
                      ref={el => { itemFileRefs.current[item.id] = el }}
                      type="file" accept="image/*" multiple capture="environment"
                      onChange={e => uploadPhoto(e, item.id)} style={{ display: 'none' }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div style={s.section}>
        <div style={s.sectionHeader}>
          <h2 style={s.sectionTitle}>General Photos</h2>
          <button style={s.uploadBtn} onClick={() => generalFileRef.current.click()} disabled={uploading === 'general'}>
            {uploading === 'general' ? 'Uploading...' : '📷 Add Photo'}
          </button>
          <input ref={generalFileRef} type="file" accept="image/*" multiple capture="environment" onChange={e => uploadPhoto(e, null)} style={{ display: 'none' }} />
        </div>
        <p style={s.internalNote}>🔒 Internal only. Not tied to a specific task.</p>
        {generalPhotos.length === 0 ? <p style={s.empty2}>No general photos yet.</p> : (
          <div style={s.photoGrid}>
            {generalPhotos.map(p => (
              <a key={p.id} href={p.url} target="_blank" rel="noreferrer">
                <img src={p.url} alt="Site visit" style={s.photo} />
              </a>
            ))}
          </div>
        )}
      </div>

      {visit.client_id && (
        <div style={{ marginTop: '16px' }}>
          <RemindersPanel clientId={visit.client_id} title="Follow-Up on This Client" />
        </div>
      )}
    </div>
  )
}

// ─── Styles ─────────────────────────────────────────────────────────────────────
const s = {
  page: { width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { margin: 0, fontSize: '28px', fontWeight: '700', color: '#1a2e1a' },
  sub: { margin: '4px 0 0', color: '#6b7c6b', fontSize: '14px' },
  filters: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' },
  filterSelect: { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: 'inherit', color: '#374151', background: 'white' },
  filterBtn: { padding: '6px 14px', borderRadius: '20px', border: '1.5px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151' },
  filterActive: { background: '#2d4a2d', color: 'white', borderColor: '#2d4a2d' },
  loading: { color: '#6b7c6b', padding: '40px 0', textAlign: 'center' },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  card: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', padding: '16px', background: 'white', borderRadius: '10px', textDecoration: 'none', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  info: { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' },
  name: { fontWeight: '600', color: '#1a2e1a', fontSize: '16px' },
  detail: { fontSize: '13px', color: '#6b7c6b' },
  urgencyBadge: { padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700' },
  empty: { textAlign: 'center', padding: '40px 0', color: '#6b7c6b' },
  empty2: { color: '#6b7c6b', fontSize: '14px' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' },
  back: { background: 'none', border: 'none', color: '#2d4a2d', cursor: 'pointer', fontSize: '14px', fontWeight: '600', padding: '0 0 16px', display: 'block' },
  profileHeader: { display: 'flex', gap: '20px', alignItems: 'flex-start', justifyContent: 'space-between', background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', flexWrap: 'wrap' },
  profileInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' },
  profileName: { margin: 0, fontSize: '24px', fontWeight: '700', color: '#1a2e1a' },
  clientLink: { color: '#2d4a2d', fontWeight: '600', fontSize: '15px', textDecoration: 'none' },
  contactRow: { display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '14px', color: '#374151' },
  headerActions: { display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' },
  addJobBtn: { background: '#2d4a2d', color: 'white', padding: '10px 18px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600', border: 'none', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', textAlign: 'center' },
  archiveBtn: { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'inherit' },
  deleteBtn: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'inherit' },
  section: { background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' },
  sectionTitle: { margin: 0, fontSize: '18px', fontWeight: '700', color: '#1a2e1a' },
  introGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px', marginTop: '16px' },
  field: {},
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '15px', fontFamily: 'inherit', boxSizing: 'border-box', background: 'white' },
  textarea: { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '15px', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' },
  saveBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit' },
  uploadBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit' },
  internalNote: { fontSize: '12px', color: '#6b7280', marginBottom: '16px' },
  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' },
  photo: { width: '100%', height: '150px', objectFit: 'cover', borderRadius: '8px', display: 'block' },
  statusBadge: { color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', flexShrink: 0 },
  taskList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  taskCard: { border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px', background: '#f9fafb' },
  taskCardHeader: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' },
  taskTitleInput: { flex: 1, padding: '8px 10px', borderRadius: '7px', border: '1.5px solid #d1d5db', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit', background: 'white' },
  taskRemoveBtn: { background: 'white', border: '1px solid #fecaca', color: '#dc2626', borderRadius: '6px', padding: '6px 9px', cursor: 'pointer', fontSize: '12px' },
  taskTextarea: { width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', background: 'white', marginBottom: '10px' },
  taskPhotoRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
  taskPhoto: { width: '64px', height: '64px', objectFit: 'cover', borderRadius: '7px', display: 'block' },
  taskAddPhotoBtn: { width: '64px', height: '64px', borderRadius: '7px', border: '1.5px dashed #d1d5db', background: 'white', cursor: 'pointer', fontSize: '13px', color: '#6b7280' },
}
